import React, { useContext, useEffect, useState } from "react";
import { Box, Typography, Grid, Divider } from "@mui/material";
import { AppContext } from "../context";
import { pathJoinUrl } from "@smfa/utils/src";

import LimitedTextField from "../components/common/LimitedTextField";
import FileUploadArea from "../components/common/FileUploadArea";
import FormActionBar from "../components/common/FormActionBar";
import RichTextEditor from "../components/RichTextEditor";
import ToolBody from "../components/common/ToolBody";

// API actions
import { addeditMission, getMissionlist } from "../actions/home.actions";

import missionimg from "../assets/img/mission-img.jpg";

/**
 * @returns {JSX.Element}
 * @component
 */
const Mission = () => {
  const { storage } = useContext(AppContext);

  // Form initial state
  const addHomeMission_default = {
    home_mission_section_id: 0,
    image: "",
    image_name: "",
    image_error: false,
    image_mobile: "",
    image_mobile_name: "",
    image_mobile_error: false,
    mission_lebel: "",
    mission_lebel_maxlenth: 255,
    mission_lebel_error: false,
    mission_title: "",
    mission_title_maxlenth: 255,
    mission_title_error: false,
    short_description: "",
    // short_description_maxlenth: 65535, // Removed max length for RichTextEditor
    short_description_error: false,
    mission_list_first: "",
    mission_list_first_maxlenth: 255,
    mission_list_first_error: false,
    mission_list_second: "",
    mission_list_second_maxlenth: 255,
    mission_list_second_error: false,
    mission_list_third: "",
    mission_list_third_maxlenth: 255,
    mission_list_third_error: false,
  };

  // Component state
  const [addHomeMission, setHomeMission] = useState(addHomeMission_default);
  const [deskimage, setDeskImage] = useState(missionimg);
  const [mobimage, setMobImage] = useState("");
  const [loader, setLoader] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // Handle text field changes
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setSuccessMsg("");
    setErrorMsg("");

    setHomeMission((prev) => {
      const maxLengthKey = `${name}_maxlenth`;
      if (!prev[maxLengthKey] || value.length <= prev[maxLengthKey]) {
        return {
          ...prev,
          [name]: value,
          [`${name}_error`]: false,
        };
      }
      return prev;
    });
  };

  // Handle RichTextEditor changes
  const handleRichTextChange = (name, content) => {
    setSuccessMsg("");
    setErrorMsg("");
    setHomeMission((prev) => ({
      ...prev,
      [name]: content,
      [`${name}_error`]: false,
    }));
  };

  // File upload handlers
  const onImageChange = (event) => {
    setSuccessMsg("");
    setErrorMsg("");

    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setDeskImage(URL.createObjectURL(file));
      setHomeMission((prev) => ({
        ...prev,
        image: file,
        image_name: file.name,
        image_error: false,
      }));
    }
  };

  const onMobImageChange = (event) => {
    setSuccessMsg("");
    setErrorMsg("");

    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setMobImage(URL.createObjectURL(file));
      setHomeMission((prev) => ({
        ...prev,
        image_mobile: file,
        image_mobile_name: file.name,
        image_mobile_error: false,
      }));
    }
  };

  const clearUpload = (field, clearPreview) => {
    setHomeMission((prev) => ({
      ...prev,
      [field]: "",
      [`${field}_name`]: "",
    }));
    clearPreview("");
  };

  // Load data on component mount
  useEffect(() => {
    getMissionlist()
      .then((res) => {
        if (res.data.status === true) {
          const data = res.data.data;

          // Create updated state with response data
          const updatedState = {
            ...addHomeMission_default,
            home_mission_section_id: data.home_mission_section_id || 0,
            mission_lebel: data.mission_lebel || "",
            mission_title: data.mission_title || "",
            short_description: data.short_description || "",
            mission_list_first: data.mission_list_first || "",
            mission_list_second: data.mission_list_second || "",
            mission_list_third: data.mission_list_third || "",
            image_name: data.image || "",
            image_mobile_name: data.image_mobile || "",
          };

          setHomeMission(updatedState);

          // Set images if available
          if (data.image) {
            setDeskImage(pathJoinUrl(storage[0].imageEndUrl, data.image));
          }
          if (data.image_mobile) {
            setMobImage(pathJoinUrl(storage[0].imageEndUrl, data.image_mobile));
          }
        }
      })
      .catch((error) => {
        setErrorMsg("Failed to load mission data");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Form submission
  const submitMission = () => {
    setLoader(true);
    setSuccessMsg("");
    setErrorMsg("");

    // Validate required fields
    const updatedState = { ...addHomeMission };
    let hasError = false;

    const requiredFields = [
      "mission_lebel",
      "mission_title",
      "short_description",
    ];

    requiredFields.forEach((field) => {
      if (!updatedState[field]) {
        updatedState[`${field}_error`] = true;
        hasError = true;
      }
    });

    if (hasError) {
      setHomeMission(updatedState);
      setLoader(false);
      return;
    }

    // Create form data
    const formData = new FormData();
    formData.append(
      "home_mission_section_id",
      updatedState.home_mission_section_id,
    );
    formData.append("image", updatedState.image);
    formData.append("image_mobile", updatedState.image_mobile);
    formData.append("mission_lebel", updatedState.mission_lebel);
    formData.append("mission_title", updatedState.mission_title);
    formData.append("short_description", updatedState.short_description);
    formData.append("mission_list_first", updatedState.mission_list_first);
    formData.append("mission_list_second", updatedState.mission_list_second);
    formData.append("mission_list_third", updatedState.mission_list_third);

    // Submit to API
    const isNew = updatedState.home_mission_section_id === 0;
    addeditMission(formData)
      .then((res) => {
        if (res.data.status === true) {
          setSuccessMsg(
            isNew
              ? "Our Mission Added Successfully!"
              : "Our Mission Updated Successfully!",
          );

          // Hide success message after delay
          setTimeout(() => {
            setSuccessMsg("");
          }, 3000);
        } else {
          setErrorMsg(
            res.data.message || "Something went wrong, please try again!",
          );
        }
      })
      .catch((error) => {
        setErrorMsg("Something went wrong, please try again!");
      })
      .finally(() => {
        setLoader(false);
      });
  };

  // Reset form
  const resetForm = () => {
    setHomeMission(addHomeMission_default);
    setDeskImage(missionimg);
    setMobImage("");
    setErrorMsg("");
    setSuccessMsg("");
  };

  return (
    <ToolBody title="Our Mission Section">
      <Grid container spacing={3}>
        {/* Label and Title */}
        <Grid size={{ xs: 12, sm: 5 }}>
          <LimitedTextField
            name="mission_lebel"
            label="Label"
            value={addHomeMission.mission_lebel}
            onChange={handleInputChange}
            maxLength={addHomeMission.mission_lebel_maxlenth}
            error={addHomeMission.mission_lebel_error}
            errorText="Please enter Label!"
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 7 }}>
          <LimitedTextField
            name="mission_title"
            label="Title"
            value={addHomeMission.mission_title}
            onChange={handleInputChange}
            maxLength={addHomeMission.mission_title_maxlenth}
            error={addHomeMission.mission_title_error}
            errorText="Please enter Title!"
          />
        </Grid>

        {/* Short Description */}
        <Grid size={{ xs: 12 }}>
          <RichTextEditor
            name="short_description"
            label="Short Description"
            data={addHomeMission.short_description}
            onChange={
              (event, editorInstance) =>
                handleRichTextChange(
                  "short_description",
                  editorInstance.getData(),
                ) // Use getData()
            }
            init={{ height: 300 }}
            error={addHomeMission.short_description_error}
            errorText="Please enter Short Description!"
          />
        </Grid>
      </Grid>

      {/* Pointers Section */}
      <Box sx={{ mt: 4, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Pointers
        </Typography>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <LimitedTextField
              name="mission_list_first"
              label="Focus Points"
              value={addHomeMission.mission_list_first}
              onChange={handleInputChange}
              maxLength={addHomeMission.mission_list_first_maxlenth}
              error={addHomeMission.mission_list_first_error}
              errorText="Please enter Focus Points!"
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <LimitedTextField
              name="mission_list_second"
              label="Focus Points"
              value={addHomeMission.mission_list_second}
              onChange={handleInputChange}
              maxLength={addHomeMission.mission_list_second_maxlenth}
              error={addHomeMission.mission_list_second_error}
              errorText="Please enter Focus Points!"
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <LimitedTextField
              name="mission_list_third"
              label="Focus Points"
              value={addHomeMission.mission_list_third}
              onChange={handleInputChange}
              maxLength={addHomeMission.mission_list_third_maxlenth}
              error={addHomeMission.mission_list_third_error}
              errorText="Please enter Focus Points!"
            />
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Images Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Images
        </Typography>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FileUploadArea
              title="Add Image For Desktop"
              description={
                <>
                  <span>Best image Resolution: 925px X 675px</span>
                  <br />
                  <span>Supported files types: JPG, JPEG & PNG</span>
                </>
              }
              previewUrl={deskimage}
              fileName={addHomeMission.image_name}
              onChange={onImageChange}
              deleteRequest={{
                resource: "home-mission",
                id: addHomeMission.home_mission_section_id,
                field: "image",
              }}
              onRemove={() => clearUpload("image", setDeskImage)}
              accept=".png,.jpg,.jpeg"
              error={addHomeMission.image_error}
              errorText="Please upload Desktop Image!"
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FileUploadArea
              title="Add Image For Mobile"
              description={
                <>
                  <span>Best image Resolution: 456px X 300px</span>
                  <br />
                  <span>Supported files types: JPG, JPEG & PNG</span>
                </>
              }
              previewUrl={mobimage}
              fileName={addHomeMission.image_mobile_name}
              onChange={onMobImageChange}
              deleteRequest={{
                resource: "home-mission",
                id: addHomeMission.home_mission_section_id,
                field: "image_mobile",
              }}
              onRemove={() => clearUpload("image_mobile", setMobImage)}
              accept=".png,.jpg,.jpeg"
              error={addHomeMission.image_mobile_error}
              errorText="Please upload Mobile Image!"
            />
          </Grid>
        </Grid>
      </Box>

      {/* Form Action Bar */}
      <FormActionBar
        onSubmit={submitMission}
        onReset={resetForm}
        isSubmitting={loader}
        isEdit={addHomeMission.home_mission_section_id !== 0}
        successMessage={successMsg}
        errorMessage={errorMsg}
        submitLabel="Update"
      />
    </ToolBody>
  );
};

export default Mission;
